import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import { Security, Warning } from "@mui/icons-material";
import { userCan, getAdminHomePath } from "../config/adminRoles";

const Screen = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      bgcolor: "#f8fafc",
      p: 3,
    }}
  >
    {children}
  </Box>
);

const AccessCard = ({ title, children, footer }) => (
  <Screen>
    <Paper
      sx={{
        p: 4,
        maxWidth: 450,
        textAlign: "center",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
        }}
      >
        <Security sx={{ fontSize: 40, color: "#ef4444" }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
        {title}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {children}
      </Typography>

      <Box
        sx={{
          p: 2,
          bgcolor: "#fef3c7",
          borderRadius: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Warning sx={{ color: "#d97706", fontSize: 20, mt: 0.3 }} />
        <Typography variant="body2" sx={{ color: "#92400e", textAlign: "left" }}>
          If you believe you should have access, please contact the system administrator.
        </Typography>
      </Box>

      {footer && <Box sx={{ mt: 3 }}>{footer}</Box>}
    </Paper>
  </Screen>
);

/**
 * AdminRouteGuard - Protects admin routes from unauthorized access
 *
 * This component ensures:
 * 1. User is authenticated
 * 2. User type is 'admin' and has an admin role
 * 3. User holds the permission this page needs (`requiredPermission`) —
 *    otherwise they are sent to the first page their role can open.
 *
 * It only decides what to show; the server checks every request itself.
 */
const AdminRouteGuard = ({ children, requiredPermission = null }) => {
  const location = useLocation();

  // Get auth state from both regular and admin auth
  const { validateUser, loginUser, adminAuth } = useSelector((state) => state);

  // Get user from various sources
  const user =
    validateUser?.UserValidate?.data?.user ||
    loginUser?.login?.data?.data ||
    adminAuth?.admin;

  const token =
    sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");

  // Loading state
  const isLoading = validateUser?.loading || loginUser?.loading || adminAuth?.loading;

  if (isLoading) {
    return (
      <Screen>
        <CircularProgress sx={{ color: "#e94560" }} />
      </Screen>
    );
  }

  // Not authenticated - redirect to admin login
  if (!token && !user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // User exists but is not admin type - show access denied
  if (user && user.type !== "admin") {
    return (
      <AccessCard
        title="Access Denied"
        footer={
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => {
              // Redirect based on user type
              if (user.type === "landlord") {
                window.location.href = "/landlord-dashboard";
              } else if (user.type === "tenant") {
                window.location.href = "/tenant-dashboard";
              } else {
                window.location.href = "/";
              }
            }}
          >
            Go to your dashboard
          </Typography>
        }
      >
        This area is restricted to administrators only. You are logged in as a{" "}
        <strong>{user.type}</strong>.
      </AccessCard>
    );
  }

  // An admin account without a role has no access to anything
  if (user && !user.adminRole) {
    return (
      <AccessCard title="No Role Assigned">
        Your admin account has not been given a role yet, so there is nothing here for you to open.
      </AccessCard>
    );
  }

  // This page needs a permission the admin's role does not carry
  if (user && requiredPermission && !userCan(user, requiredPermission)) {
    const home = getAdminHomePath(user);

    if (home && home !== location.pathname) {
      return <Navigate to={home} replace />;
    }

    return (
      <AccessCard title="Access Denied">
        Your role does not include this page.
      </AccessCard>
    );
  }

  // User is admin and allowed here
  return children;
};

export default AdminRouteGuard;
